import { useEffect, useState, useRef } from "react";
import InventoryCategoryUI from "./InventoryCategoryUI";
import * as toast from "../../../Wrapper/toast/toast";
import * as apiHelper from "../../../Services/ApiHelper";
import * as stringConstants from "../../../Constants/stringConstants";
import { changeDateFormatForServer } from "../../../Utils/dateUtils";
import * as stringUtils from "../../../Utils/stringUtils";
import * as commonServices from "../../../Services/common";

import * as storageConstants from "../../../Constants/storageConstant";

const InventoryCategory = () => {
  const [modalShow, setModalShow] = useState(false);
  const [inventoryList, setInventoryList] = useState([]);
  const [dates, setDates] = useState({ startDate: null, endDate: null });
  const [searchQuery, setSearchQuery] = useState("");
  const [lastSelectedPage, setLastSelectedPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [importModal, setImportModal] = useState(false);
  const [importList, setImportList] = useState();
  const [selectedRows, setSelectedRows] = useState([]);

  const datePickerStartRef = useRef();
  const datePickerEndRef = useRef();

  useEffect(() => {
    const last_visited_page = commonServices.getLocalData(storageConstants.LAST_VISITED_PAGE) || {};
    const inventoryCategoryState = last_visited_page["inventory_categories"] || null;

    if (inventoryCategoryState) {
      if (inventoryCategoryState.dates) setDates(inventoryCategoryState.dates);
      if (inventoryCategoryState.searchQuery) setSearchQuery(inventoryCategoryState.searchQuery);
      if (inventoryCategoryState.page) setLastSelectedPage(inventoryCategoryState.page);
    }
  }, []);

  useEffect(() => {
    const last_visited_page = commonServices.getLocalData(storageConstants.LAST_VISITED_PAGE) || {};
    last_visited_page["inventory_categories"] = {
      dates,
      searchQuery,
      page: lastSelectedPage,
    };
    commonServices.storeLocalData(storageConstants.LAST_VISITED_PAGE, last_visited_page);
  }, [dates, searchQuery, lastSelectedPage]);

  const handleClose = () => {
    setModalShow(false);
    clearData();
  };

  const initialAddInventoryData = {
    name: "",
    description: "",
    action: "Add",
    id: null,
    status: "",
    type_id: null,
    billable: true, // <-- new field
  };

  const [addInventoryData, setAddInventoryData] = useState(initialAddInventoryData);

  useEffect(() => {
    if (
      (dates.startDate !== null && dates.endDate !== null) ||
      (dates.startDate === null && dates.endDate === null)
    ) {
      getInventoryCategoryList(lastSelectedPage);
    }
  }, [dates]);

  const onPageChange = (page) => {
    getInventoryCategoryList(page);
  };

  const getInventoryCategoryList = (page = 1) => {
    const query = `?page=${page}&limit=10&start_date=${changeDateFormatForServer(dates.startDate)}&end_date=${changeDateFormatForServer(dates.endDate)}&q=${searchQuery}`;
    setLoading(true);
    setLastSelectedPage(page);
    apiHelper
      .getInventoryCategoryList(query)
      .then((response) => {
        if (response?.isSuccess) {
          setInventoryList(response?.data);
        } else {
          toast.warn(response?.message);
        }
      })
      .catch((err) => toast.warn(err))
      .finally(() => setLoading(false));
  };

  const clearData = () => {
    setAddInventoryData(initialAddInventoryData);
  };

  const validateData = () => {
    const { name } = addInventoryData;
    let validData = true;

    if (stringUtils.isNullOrEmpty(name)) {
      toast.warn(stringConstants.INVENTORY_CATEGORY_NAME_REQUIRED);
      validData = false;
    } else if (!stringUtils.isValidName(name)) {
      toast.warn(stringConstants.INVENTORY_CATEGORY_LENGTH);
      validData = false;
    }

    return validData;
  };

  const addInventory = () => {
    if (validateData()) {
      setButtonLoading(true);
      const { name, description, type_id, billable } = addInventoryData;
      apiHelper
        .addInventoryCategory({ name, description, type_id, billable })
        .then((response) => {
          setButtonLoading(false);
          if (response?.isSuccess) {
            getInventoryCategoryList(lastSelectedPage);
            commonServices.fetchSystemData();
            toast.success(response.message);
            setModalShow(false);
            clearData();
          } else {
            toast.warn(response?.message);
          }
        })
        .catch((error) => {
          toast.warn(error);
        })
        .finally(() => {
          setButtonLoading(false);
        });
    }
  };

  const Action = (data, action = "") => {
    if (action === "edit") {
      setAddInventoryData({
        name: data.name,
        description: data.description,
        action: "Edit",
        id: data.id,
        billable: data.billable ?? true,
      });

      setModalShow(true);
    }
  };

  const editInventory = (id) => {
    if (validateData()) {
      setButtonLoading(true);
      const { name, description, type_id, billable } = addInventoryData;
      apiHelper
        .editInventoryCategory(id, { name, description, type_id: 1, billable })
        .then((response) => {
          setButtonLoading(false);
          if (response?.isSuccess) {
            getInventoryCategoryList(lastSelectedPage);
            commonServices.fetchSystemData();
            toast.success(response.message);
            setModalShow(false);
            clearData();
          } else {
            toast.warn(response?.message);
          }
        });
    }
  };
  const importListing = (data) => {
    if (importModal) {
      setLoading(true);
      apiHelper
        .importMaster("inventory_type", data)
        .then((response) => {
          if (response?.isSuccess) {
            setImportList(response?.data);
          }
        })
        .catch((err) => toast.warn(err))
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const openImportModal = () => {
    setImportModal(true);
  };

  const exportList = () => {
    setButtonLoading(true);
    apiHelper
      .exportMaster("inventory_type", { ids: selectedRows })
      .then((response) => {
        if (response?.isSuccess) {
          toast.success(response?.message);
          getInventoryCategoryList(lastSelectedPage);
          commonServices.fetchSystemData(setLoading);
        } else {
          toast.warn(response.message);
        }
      })
      .catch((err) => toast.warn(err))
      .finally(() => {
        setButtonLoading(false);
        setImportModal(false);
      });
  };

  const statusHandler = (data) => {
    const updatedStatus = data.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    setAddInventoryData({ ...addInventoryData, status: updatedStatus });
    setButtonLoading(true);

    apiHelper.changeInventoryCategoryStatus(data.id, { status: updatedStatus }).then((response) => {
      setButtonLoading(false);
      if (response?.isSuccess) {
        getInventoryCategoryList(lastSelectedPage);
        commonServices.fetchSystemData();
        toast.success(response.message);
        setModalShow(false);
        clearData();
      } else {
        toast.warn(response?.message);
      }
    });
  };

  return (
    <>
      <InventoryCategoryUI
        modalShow={modalShow}
        setModalShow={setModalShow}
        handleClose={handleClose}
        inventoryList={inventoryList}
        addInventoryData={addInventoryData}
        setAddInventoryData={setAddInventoryData}
        dates={dates}
        setDates={setDates}
        datePickerStartRef={datePickerStartRef}
        datePickerEndRef={datePickerEndRef}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        addInventory={addInventory}
        onPageChange={onPageChange}
        editInventory={editInventory}
        loading={loading}
        buttonLoading={buttonLoading}
        Action={Action}
        showConfirmModal={showConfirmModal}
        setShowConfirmModal={setShowConfirmModal}
        getInventoryCategoryList={getInventoryCategoryList}
        statusHandler={statusHandler}
        importListing={importListing}
        exportList={exportList}
        importModal={importModal}
        importList={importList}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        setImportModal={setImportModal}
        openImportModal={openImportModal}
      />
    </>
  );
};

export default InventoryCategory;
